import { FormattedMessage, defineMessages, useIntl } from "react-intl";
import {
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
} from "@fluentui/react-components";

import type { Interview } from "@aglocal/schema/Interview";
import type { DarkquellInferredSkill } from "@aglocal/schema/Darkquell";

import useTranscript from "@aglocal/web/firebase/queries/useTranscript";
import useReportError from "@aglocal/web/hooks/useReportError";

import Callout from "@aglocal/web/components/Callout";

import Transcript from "@/components/Transcript";

const messages = defineMessages({
  loadError: {
    defaultMessage: "Error loading transcript",
    description:
      "Shown in the drawer when the meeting transcript could not be loaded.",
  },
  loading: {
    defaultMessage: "Loading transcript",
    description: "Shown in the drawer while the meeting transcript loads.",
  },
  optedOut: {
    defaultMessage:
      "{name} has opted out of being recorded for this meeting, transcripts were not captured.",
    description:
      "Shown in the drawer instead of the transcript when the talent refused to be recorded; {name} is the talent's name.",
  },
  talent: {
    defaultMessage: "The talent",
    description: "Stands in for the talent's name when it is unknown.",
  },
  unavailable: {
    defaultMessage: "Transcript not available",
    description: "Shown in the drawer when the meeting has no transcript.",
  },
});

interface TranscriptPanelContentProps {
  interview: Interview;
  skills: readonly DarkquellInferredSkill[];
}

function TranscriptPanelContent({
  interview,
  skills,
}: TranscriptPanelContentProps) {
  const { $t } = useIntl();
  const { transcript, loading, error } = useTranscript(interview.id, interview);

  useReportError(error);

  if (error) {
    return <Callout intent="error" title={$t(messages.loadError)} />;
  }

  if (loading) {
    return <Callout intent="info" title={$t(messages.loading)} />;
  }

  if (!transcript?.length) {
    if (interview.interviewee?.recordOptOut === true) {
      const name = interview.interviewee.displayName || $t(messages.talent);
      return (
        <Callout intent="warning" title={$t(messages.optedOut, { name })} />
      );
    }
    return <Callout intent="warning" title={$t(messages.unavailable)} />;
  }

  return (
    <Transcript interview={interview} transcript={transcript} skills={skills} />
  );
}

export interface TranscriptAccordionItemProps {
  name: unknown;
  interview: Interview;
  skills: readonly DarkquellInferredSkill[];
}

export default function TranscriptAccordionItem({
  name,
  interview,
  skills,
}: TranscriptAccordionItemProps) {
  return (
    <AccordionItem value={name}>
      <AccordionHeader>
        <FormattedMessage
          defaultMessage="Meeting Transcript"
          description="Header of the drawer section showing the meeting transcript."
        />
      </AccordionHeader>
      <AccordionPanel>
        <TranscriptPanelContent interview={interview} skills={skills} />
      </AccordionPanel>
    </AccordionItem>
  );
}
